import { useState, useEffect } from 'react';

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length <= 500) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setMessageLength(value.length);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d48m1h84bso62du6dp30', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setMessageLength(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const callNow = () => {
    window.location.href = 'tel:647-558-2086';
  };

  const openReadyAgent = () => {
    const button = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (button) {
      button.click();
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-blue-400/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                <i className="ri-car-washing-line text-black text-xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-white">
                <span>Polarist</span>
                <span className="text-blue-400 ml-2">Mobile Detailing</span>
              </h1>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-white hover:text-blue-400 transition-colors cursor-pointer">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-white hover:text-blue-400 transition-colors cursor-pointer">About</button>
              <button onClick={() => scrollToSection('services')} className="text-white hover:text-blue-400 transition-colors cursor-pointer">Services</button>
              <button onClick={() => scrollToSection('gallery')} className="text-white hover:text-blue-400 transition-colors cursor-pointer">Gallery</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-white hover:text-blue-400 transition-colors cursor-pointer">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="text-white hover:text-blue-400 transition-colors cursor-pointer">Contact</button>
              <button onClick={callNow} className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-blue-400 to-blue-600 text-black hover:from-blue-500 hover:to-blue-700 shadow-lg hover:shadow-xl px-4 py-2 text-sm rounded-md">
                Book Now
              </button>
            </nav>

            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white text-2xl cursor-pointer"
            >
              <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-700">
              <nav className="flex flex-col space-y-4 mt-4">
                <button onClick={() => scrollToSection('home')} className="text-white hover:text-blue-400 transition-colors cursor-pointer text-left">Home</button>
                <button onClick={() => scrollToSection('about')} className="text-white hover:text-blue-400 transition-colors cursor-pointer text-left">About</button>
                <button onClick={() => scrollToSection('services')} className="text-white hover:text-blue-400 transition-colors cursor-pointer text-left">Services</button>
                <button onClick={() => scrollToSection('gallery')} className="text-white hover:text-blue-400 transition-colors cursor-pointer text-left">Gallery</button>
                <button onClick={() => scrollToSection('testimonials')} className="text-white hover:text-blue-400 transition-colors cursor-pointer text-left">Reviews</button>
                <button onClick={() => scrollToSection('contact')} className="text-white hover:text-blue-400 transition-colors cursor-pointer text-left">Contact</button>
                <button onClick={callNow} className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-blue-400 to-blue-600 text-black hover:from-blue-500 hover:to-blue-700 shadow-lg hover:shadow-xl px-4 py-2 text-sm rounded-md w-fit">
                  Book Now
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden">
          <div className="container mx-auto px-4 py-20 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                <span className="text-blue-400">Luxury</span> Auto Detailing
                <br />
                <span className="text-3xl md:text-5xl font-light">at Your Doorstep</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                Professional mobile detailing service bringing showroom-level cleaning and protection directly to your location
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button 
                  onClick={callNow}
                  className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-green-500 hover:bg-green-600 text-white px-8 py-4 text-lg rounded-xl transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <i className="ri-phone-line mr-2"></i>Call Now: (647) 558-2086
                </button>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-black px-8 py-4 text-lg rounded-xl transform hover:scale-105"
                >
                  <i className="ri-eye-line mr-2"></i>View Services
                </button>
              </div>

              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-map-pin-line text-blue-400 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">100% Mobile</h3>
                  <p className="text-gray-400 text-sm">We come to your location</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-blue-400 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Guaranteed</h3>
                  <p className="text-gray-400 text-sm">Satisfaction guaranteed</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-award-line text-blue-400 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Professional</h3>
                  <p className="text-gray-400 text-sm">Premium products & equipment</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-blue-400 text-2xl"></i>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                  About <span className="text-blue-600">Polarist Mobile Detailing</span>
                </h2>
                <div className="w-24 h-1 bg-blue-400 mx-auto mb-8"></div>
              </div>

              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <img 
                    alt="Professional auto detailing service" 
                    className="rounded-2xl shadow-2xl object-cover w-full h-96" 
                    src="https://public.readdy.ai/ai/img_res/73a43a2c077681cd56b06a64e7fe89ae.jpg"
                  />
                </div>
                <div className="space-y-6">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    At Polarist Mobile Detailing, we're committed to delivering exceptional automotive care that exceeds your expectations. Our team of skilled professionals brings years of experience and passion for perfection to every vehicle we service.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    We understand that your vehicle is more than just transportation—it's an investment that deserves the highest level of care. That's why we use only premium products and state-of-the-art equipment to ensure your car receives the royal treatment it deserves.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-6 mt-8">
                    <div className="text-center p-6 bg-white rounded-xl shadow-lg">
                      <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
                      <div className="text-gray-600">Cars Detailed</div>
                    </div>
                    <div className="text-center p-6 bg-white rounded-xl shadow-lg">
                      <div className="text-3xl font-bold text-blue-600 mb-2">100%</div>
                      <div className="text-gray-600">Satisfaction Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-black">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Our <span className="text-blue-400">Services</span>
              </h2>
              <div className="w-24 h-1 bg-blue-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Choose from our range of professional detailing services, all performed at your location
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Regular Wash */}
              <div className="relative bg-gray-900 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-gray-700 hover:border-blue-400/50">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-blue-400/20">
                    <i className="ri-car-washing-line text-2xl text-blue-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Regular Wash</h3>
                  <div className="text-3xl font-bold text-blue-400 mb-4">$149.99</div>
                  <p className="text-gray-400 mb-6">Exterior wash, rim and tire cleaning, and light interior wipe-down</p>
                </div>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>Exterior wash & dry
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>Rim & tire cleaning
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>Light interior wipe-down
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>Window cleaning
                  </li>
                </ul>
                
                <button 
                  onClick={callNow}
                  className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-black px-6 py-3 text-base rounded-lg w-full"
                >
                  Book Now
                </button>
              </div>

              {/* Interior Only */}
              <div className="relative bg-gray-900 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-gray-700 hover:border-blue-400/50">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-blue-400/20">
                    <i className="ri-brush-line text-2xl text-blue-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Interior Only</h3>
                  <div className="text-3xl font-bold text-blue-400 mb-4">Starting at $189.99</div>
                  <p className="text-gray-400 mb-6">Comprehensive interior detailing for all vehicle types</p>
                </div>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>Sedan - $189.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>SUV 5-seater - $214.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>7-seater & Truck - $229.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>Mini Van - $249.99
                  </li>
                </ul>
                
                <button 
                  onClick={callNow}
                  className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-black px-6 py-3 text-base rounded-lg w-full"
                >
                  Book Now
                </button>
              </div>

              {/* Showroom Package */}
              <div className="relative bg-gray-900 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-blue-400 shadow-2xl shadow-blue-400/20">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-400 text-black px-4 py-2 rounded-full text-sm font-bold">Most Popular</span>
                </div>
                
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-blue-400">
                    <i className="ri-star-line text-2xl text-black"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Showroom Package</h3>
                  <div className="text-3xl font-bold text-blue-400 mb-4">Starting at $239.99</div>
                  <p className="text-gray-400 mb-6">Complete inside and outside detailing service</p>
                </div>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>Sedan - $239.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>SUV 5-seater - $264.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>7-seater & Truck - $279.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-blue-400 mr-3"></i>Mini Van - $299.99
                  </li>
                </ul>
                
                <button 
                  onClick={callNow}
                  className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-blue-400 to-blue-600 text-black hover:from-blue-500 hover:to-blue-700 shadow-lg hover:shadow-xl px-6 py-3 text-base rounded-lg w-full"
                >
                  Book Now
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section id="gallery" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                Before & <span className="text-blue-600">After Gallery</span>
              </h2>
              <div className="w-24 h-1 bg-blue-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                See the incredible transformations we achieve with our professional detailing services
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Interior Deep Clean */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Interior Deep Clean</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Interior Deep Clean - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/dc7f0d1e3d4ef1a85759633a824897fe.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Interior Deep Clean - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/65dace0b12a7c4cda0225b9ec5efabf4.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exterior Detail */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Exterior Detail</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Exterior Detail - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8dda04267350ea5ad3a40bf6c884281d.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Exterior Detail - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8dda04267350ea5ad3a40bf6c884281d.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SUV Complete Detail */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">SUV Complete Detail</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="SUV Complete Detail - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8609aae2015379ebb1d01a8c1fa67a9a.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="SUV Complete Detail - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/854be7e3dba04760f5d9ed0529753ceb.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Paint Restoration */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Paint Restoration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Paint Restoration - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://public.readdy.ai/ai/img_res/e27b3530b67aecabfe947b498885eadc.jpg"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Paint Restoration - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://public.readdy.ai/ai/img_res/e4806feb1b6921c3255c9d38baff7521.jpg"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-20 bg-black">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                What Our <span className="text-blue-400">Customers Say</span>
              </h2>
              <div className="w-24 h-1 bg-blue-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Don't just take our word for it - hear from our satisfied customers across the GTA
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {/* Testimonial 1 */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-blue-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-black font-bold text-lg">M</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Michael Chen</h4>
                    <p className="text-gray-400 text-sm">Toronto, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "Absolutely incredible service! My BMW looks better than the day I bought it. The team was professional, punctual, and the attention to detail was outstanding. Will definitely be using Polarist Mobile Detailing again!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-blue-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>BMW 3 Series
                  </p>
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-blue-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-black font-bold text-lg">S</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Sarah Johnson</h4>
                    <p className="text-gray-400 text-sm">Mississauga, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "I was amazed by the transformation of my SUV's interior. The kids had made quite a mess, but Polarist made it look brand new. The convenience of mobile service is unbeatable!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-blue-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Honda Pilot
                  </p>
                </div>
              </div>

              {/* Testimonial 3 */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-blue-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-black font-bold text-lg">D</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">David Rodriguez</h4>
                    <p className="text-gray-400 text-sm">Markham, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "Professional, reliable, and exceptional results. My Mercedes has never looked better. The paint correction work was flawless and the customer service was top-notch. Highly recommended!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-blue-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Mercedes C-Class
                  </p>
                </div>
              </div>

              {/* Testimonial 4 */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-blue-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-black font-bold text-lg">E</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Emily Thompson</h4>
                    <p className="text-gray-400 text-sm">Vaughan, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "The combo package was worth every penny. Both interior and exterior detailing exceeded my expectations. The team arrived on time and worked with such precision. My car feels like new again!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-blue-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Audi Q5
                  </p>
                </div>
              </div>

              {/* Testimonial 5 */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-blue-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-black font-bold text-lg">J</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">James Wilson</h4>
                    <p className="text-gray-400 text-sm">Brampton, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "Outstanding service from start to finish. The booking process was easy, and the results speak for themselves. My truck's interior was completely transformed. These guys are true professionals!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-blue-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Ford F-150
                  </p>
                </div>
              </div>

              {/* Testimonial 6 */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-blue-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-black font-bold text-lg">L</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Lisa Park</h4>
                    <p className="text-gray-400 text-sm">Richmond Hill, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                  <i className="ri-star-fill text-blue-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "I've tried other detailing services before, but Polarist is in a league of their own. The attention to detail and quality of work is exceptional. My Lexus has never looked this good!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-blue-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Lexus RX
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <div className="inline-flex items-center space-x-8 bg-gray-900 rounded-2xl p-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-400 mb-2">4.9/5</div>
                  <div className="text-gray-300">Average Rating</div>
                </div>
                <div className="w-px h-16 bg-gray-700"></div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-400 mb-2">500+</div>
                  <div className="text-gray-300">Happy Customers</div>
                </div>
                <div className="w-px h-16 bg-gray-700"></div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-400 mb-2">100%</div>
                  <div className="text-gray-300">Satisfaction Rate</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                Book Your <span className="text-blue-600">Appointment</span>
              </h2>
              <div className="w-24 h-1 bg-blue-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto mb-8">
                Ready to give your vehicle the royal treatment? Call us now or schedule online
              </p>
              
              <div className="flex justify-center mb-12">
                <div className="bg-white rounded-full p-1 shadow-lg">
                  <button 
                    onClick={callNow}
                    className="px-8 py-4 rounded-full font-semibold transition-all whitespace-nowrap bg-green-500 text-white hover:bg-green-600 shadow-md mr-2"
                  >
                    <i className="ri-phone-line mr-2"></i>Call Now: (647) 558-2086
                  </button>
                  <button 
                    onClick={openReadyAgent}
                    className="px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap text-gray-600 hover:text-black hover:bg-gray-100"
                  >
                    <i className="ri-calendar-line mr-2"></i>Schedule Online
                  </button>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              <div className="space-y-8">
                {/* Contact Info */}
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <h3 className="text-2xl font-bold text-black mb-6">Get In Touch</h3>
                  <div className="space-y-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-phone-line text-black text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Phone</h4>
                        <button 
                          onClick={callNow}
                          className="text-green-600 hover:text-green-700 font-semibold cursor-pointer"
                        >
                          (647) 558-2086
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-mail-line text-black text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Email</h4>
                        <p className="text-gray-600">info@polaristdetailing.com</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-map-pin-line text-black text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Service Area</h4>
                        <p className="text-gray-600">Greater Toronto Area</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-time-line text-black text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Hours</h4>
                        <p className="text-gray-600">Mon-Sat: 8AM-6PM<br />Sun: 10AM-4PM</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <button 
                      onClick={callNow}
                      className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-xl transition-colors cursor-pointer shadow-lg"
                    >
                      <i className="ri-phone-line mr-3 text-xl"></i>Call Now for Instant Booking
                      <div className="text-sm font-normal mt-1">(647) 558-2086</div>
                    </button>
                  </div>
                </div>

                {/* Why Choose Us */}
                <div className="bg-black rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Why Choose Polarist?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <i className="ri-check-line text-blue-400 mr-3"></i>100% mobile service - we come to you
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-blue-400 mr-3"></i>Professional‑grade products & equipment
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-blue-400 mr-3"></i>Satisfaction guaranteed
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-blue-400 mr-3"></i>Fully insured & bonded
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-blue-400 mr-3"></i>Eco‑friendly cleaning solutions
                    </li>
                  </ul>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-bold text-black mb-6">Send Us a Message</h3>
                
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                    Thank you! Your message has been sent successfully. We'll get back to you soon.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    Sorry, there was an error sending your message. Please try again or call us directly.
                  </div>
                )}
                
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                        placeholder="(647) 123-4567"
                      />
                    </div>
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                        Service Interested In
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select a service</option>
                        <option value="Regular Wash">Regular Wash</option>
                        <option value="Interior Only">Interior Only</option>
                        <option value="Showroom Package">Showroom Package</option>
                        <option value="Custom Quote">Custom Quote</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm resize-none"
                      placeholder="Tell us about your vehicle and any specific requirements..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{messageLength}/500 characters</p>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black text-white py-16">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8 mb-12">
              <div className="md:col-span-2">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                    <i className="ri-car-washing-line text-black text-xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold">
                    <span>Polarist</span>
                    <span className="text-blue-400 ml-2">Mobile Detailing</span>
                  </h3>
                </div>
                <p className="text-gray-400 mb-6 max-w-md">
                  Professional mobile auto detailing service bringing luxury car care directly to your doorstep. Serving the Greater Toronto Area with premium products and exceptional service.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-400 hover:text-black transition-colors cursor-pointer">
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-400 hover:text-black transition-colors cursor-pointer">
                    <i className="ri-instagram-line"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-400 hover:text-black transition-colors cursor-pointer">
                    <i className="ri-twitter-line"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-400 hover:text-black transition-colors cursor-pointer">
                    <i className="ri-google-line"></i>
                  </a>
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold mb-6 text-blue-400">Quick Links</h4>
                <ul className="space-y-3">
                  <li>
                    <button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-blue-400 transition-colors cursor-pointer">
                      Home
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-blue-400 transition-colors cursor-pointer">
                      About Us
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-blue-400 transition-colors cursor-pointer">
                      Services
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('gallery')} className="text-gray-400 hover:text-blue-400 transition-colors cursor-pointer">
                      Gallery
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-blue-400 transition-colors cursor-pointer">
                      Contact
                    </button>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold mb-6 text-blue-400">Contact Info</h4>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-400">
                    <i className="ri-phone-line mr-3 text-blue-400"></i>(647) 558-2086
                  </li>
                  <li className="flex items-center text-gray-400">
                    <i className="ri-mail-line mr-3 text-blue-400"></i>info@polaristdetailing.com
                  </li>
                  <li className="flex items-center text-gray-400">
                    <i className="ri-map-pin-line mr-3 text-blue-400"></i>Greater Toronto Area
                  </li>
                  <li className="flex items-center text-gray-400">
                    <i className="ri-time-line mr-3 text-blue-400"></i>Mon-Sat: 8AM-6PM
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-gray-800 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <p className="text-gray-400 text-sm mb-4 md:mb-0">
                  © 2025 Polarist Mobile Detailing. All rights reserved.
                </p>
                <div className="flex items-center space-x-6">
                  <a href="#" className="text-gray-400 hover:text-blue-400 text-sm transition-colors cursor-pointer">
                    Privacy Policy
                  </a>
                  <a href="#" className="text-gray-400 hover:text-blue-400 text-sm transition-colors cursor-pointer">
                    Terms of Service
                  </a>
                  <a 
                    href="https://readdy.ai/?origin=logo" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-gray-400 hover:text-blue-400 text-sm transition-colors cursor-pointer"
                  >
                    Website Builder
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}